import { ArrowRight, Check, Copy, ExternalLink, Key } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface ExistingApiKey {
  id: number;
  name: string;
  tokenPrefix: string;
}

interface Domain {
  id: number;
  hostname: string;
  verification_status?: string;
}

interface ApiKeyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  newApiKeyToken: string | null;
  existingApiKey: ExistingApiKey | null;
  primaryDomain?: Domain | null;
  onComplete: () => void;
}

export function ApiKeyModal({
  open,
  onOpenChange,
  newApiKeyToken,
  existingApiKey,
  primaryDomain,
  onComplete,
}: ApiKeyModalProps) {
  const [copiedToken, setCopiedToken] = useState(false);

  const handleCopyToken = async () => {
    if (!newApiKeyToken) return;
    await navigator.clipboard.writeText(newApiKeyToken);
    setCopiedToken(true);
    toast.success("API key copied to clipboard");
    setTimeout(() => setCopiedToken(false), 2000);
  };

  const domainName = primaryDomain?.hostname || "yourdomain.com";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-orange-100 dark:bg-orange-500/20">
            <Key className="h-6 w-6 text-orange-600 dark:text-orange-400" />
          </div>
          <DialogTitle className="text-center text-xl">
            {newApiKeyToken ? "Your API Key is Ready!" : "You already have an API key"}
          </DialogTitle>
          <DialogDescription className="text-center">
            {newApiKeyToken
              ? "Copy your API key now — you won't be able to see it again."
              : "Use your existing API key to start generating OG images."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {newApiKeyToken && (
            <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-950">
              <div className="flex items-center gap-2">
                <code className="flex-1 rounded bg-green-100 px-3 py-2 font-mono text-sm break-all dark:bg-green-900">
                  {newApiKeyToken}
                </code>
                <Button size="sm" variant="outline" onClick={() => void handleCopyToken()} className="shrink-0">
                  {copiedToken ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          )}

          {existingApiKey && !newApiKeyToken && (
            <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4 dark:border-neutral-700 dark:bg-neutral-800">
              <div className="flex items-center gap-2">
                <span className="text-sm text-neutral-500">Key:</span>
                <code className="rounded bg-neutral-100 px-2 py-1 font-mono text-sm dark:bg-neutral-900">
                  {existingApiKey.tokenPrefix}••••••••
                </code>
              </div>
            </div>
          )}

          {/* Quick start */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-neutral-900 dark:text-white">Quick Start</h4>

            <div className="space-y-2">
              <p className="text-xs font-medium text-neutral-600 dark:text-neutral-400">1. Sign a JWT payload</p>
              <pre className="overflow-x-auto rounded-lg bg-neutral-100 p-3 font-mono text-xs dark:bg-neutral-800">
                {`{
  "iss": "${domainName}",
  "sub": "YOUR_KEY_PREFIX",
  "title": "My Page Title"
}`}
              </pre>
            </div>

            <div className="space-y-2">
              <p className="text-xs font-medium text-neutral-600 dark:text-neutral-400">2. Call the API server-side</p>
              <pre className="overflow-x-auto rounded-lg bg-neutral-100 p-3 font-mono text-xs dark:bg-neutral-800">
                {`curl -X POST "https://ogpilot.com/api/v1/images" \\
  -H "Content-Type: application/json" \\
  -d '{"token":"YOUR_JWT_TOKEN"}'`}
              </pre>
            </div>

            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => window.open("/docs", "_blank")}>
              <ExternalLink className="h-3.5 w-3.5" />
              Full Documentation
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={onComplete} className="w-full gap-2">
            Continue to Dashboard
            <ArrowRight className="h-4 w-4" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
